import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Save, Loader2, Globe, Upload, Trash2, ArrowUp, ArrowDown, ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { IMAGE_UPLOAD_ACCEPT } from '@/lib/uploadAccept';

export default function BrandPagesPage() {
  const [pages, setPages] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({});
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => { load(); }, []);
  const load = async () => { try { const r = await api.get('/admin/brand-pages'); setPages(r.data); } catch {} finally { setLoading(false); } };

  const selectPage = (p) => {
    setSelected(p);
    setForm({ seo_title: p.seo_title || '', hero_title: p.hero_title || '', hero_subtitle: p.hero_subtitle || '', body_content: p.body_content || '', seo_description: p.seo_description || '' });
    setMedia([]);
    api.get(`/admin/brand-pages/${p.slug}/media`).then(r => setMedia((r.data.media_refs || []).filter(m => (m.type || 'image') === 'image'))).catch(() => {});
  };

  const handleSave = async () => {
    if (!selected) return; setSaving(true);
    try { await api.put(`/admin/brand-pages/${selected.slug}`, form); toast.success('Brand page saved'); await load(); } catch { toast.error('Failed'); } finally { setSaving(false); }
  };

  const saveMedia = async (refs) => {
    try {
      const r = await api.put(`/admin/brand-pages/${selected.slug}/media`, {
        media_refs: refs.map((m, i) => ({ type: m.type || 'image', storage_key: m.storage_key, caption: m.caption || '', sort_order: i })),
      });
      setMedia((r.data.media_refs || []).filter(m => (m.type || 'image') === 'image'));
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save images'); }
  };

  const uploadMedia = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !selected) return;
    setUploading(true);
    try {
      let latest = media;
      for (const f of files) {
        const fd = new FormData();
        fd.append('file', f);
        const r = await api.post(`/admin/brand-pages/${selected.slug}/media/upload`, fd);
        latest = (r.data.media_refs || []).filter(m => (m.type || 'image') === 'image');
      }
      setMedia(latest);
      toast.success(`${files.length} image${files.length > 1 ? 's' : ''} uploaded`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const removeMedia = (idx) => saveMedia(media.filter((_, i) => i !== idx));
  const moveMedia = (idx, dir) => {
    const next = [...media];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    saveMedia(next);
  };
  const setCaption = (idx, caption) => setMedia(media.map((m, i) => i === idx ? { ...m, caption } : m));

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;

  return (
    <div data-testid="brand-pages-page">
      <div className="flex items-center gap-2 mb-4"><Globe className="h-4 w-4 text-ink" /><h1 className="text-base font-semibold text-ink">Brand Pages</h1><Badge variant="outline" className="text-[10px]">{pages.length} pages</Badge></div>
      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-4">
        <Card className="bg-white border-stone/50"><CardContent className="p-2">
          {pages.map(p => (
            <button key={p.slug} onClick={() => selectPage(p)} className={`w-full text-left px-2 py-1.5 rounded text-xs transition-colors ${selected?.slug === p.slug ? 'bg-secondary text-ink font-medium' : 'text-muted-foreground hover:text-ink hover:bg-secondary/60'}`} data-testid={`brand-page-item-${p.slug}`}>
              <span>{p.brand_name}</span><br/><span className="text-[10px] font-mono">/{p.slug}</span>
            </button>
          ))}
        </CardContent></Card>
        {selected ? (
          <div className="space-y-4">
            <Card className="bg-white border-stone/50"><CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold text-ink">{selected.brand_name}</h2>
                <Button size="sm" className="h-7 text-xs gap-1" onClick={handleSave} disabled={saving} data-testid="brand-page-save-btn"><Save className="h-3 w-3" /> {saving ? 'Saving...' : 'Save'}</Button>
              </div>
              <div className="space-y-3">
                <div><Label className="text-xs">SEO Title</Label><Input value={form.seo_title} onChange={e => setForm({...form, seo_title: e.target.value})} className="mt-1 h-8 text-xs" /></div>
                <div><Label className="text-xs">SEO Description</Label><Input value={form.seo_description} onChange={e => setForm({...form, seo_description: e.target.value})} className="mt-1 h-8 text-xs" /></div>
                <div><Label className="text-xs">Hero Title</Label><Input value={form.hero_title} onChange={e => setForm({...form, hero_title: e.target.value})} className="mt-1 h-8 text-xs" /></div>
                <div><Label className="text-xs">Hero Subtitle</Label><Input value={form.hero_subtitle} onChange={e => setForm({...form, hero_subtitle: e.target.value})} className="mt-1 h-8 text-xs" /></div>
                <div><Label className="text-xs">Body Content (HTML)</Label><textarea value={form.body_content} onChange={e => setForm({...form, body_content: e.target.value})} className="w-full mt-1 p-3 rounded border border-stone text-xs font-mono min-h-[200px] resize-y focus:outline-none focus:ring-2 focus:ring-ring/30" style={{background:'hsl(var(--bg-paper))'}} /></div>
              </div>
            </CardContent></Card>

            <Card className="bg-white border-stone/50"><CardContent className="p-4" data-testid="brand-page-media-section">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><ImageIcon className="h-3.5 w-3.5" /> Page Images ({media.length})</h3>
                <div>
                  <input id="brand-media-file" type="file" accept={IMAGE_UPLOAD_ACCEPT} multiple className="hidden" onChange={uploadMedia} data-testid="brand-media-upload-input" />
                  <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => document.getElementById('brand-media-file').click()} disabled={uploading} data-testid="brand-media-upload-btn">
                    {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />} Upload images
                  </Button>
                </div>
              </div>
              {media.length === 0 ? (
                <p className="text-xs text-muted-foreground">No custom images — the public page uses the default curated set. Upload images to replace them. The first image leads the hero collage.</p>
              ) : (
                <>
                  <p className="text-[11px] text-muted-foreground mb-2">These replace the default images on the public /brands/{selected.slug} page, in this order (first = hero).</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {media.map((m, idx) => (
                      <div key={m.storage_key || idx} className="rounded-lg border border-stone/30 overflow-hidden bg-stone/5" data-testid={`brand-media-item-${idx}`}>
                        <div className="aspect-square overflow-hidden relative">
                          <img src={m.url} alt={m.caption || `image ${idx + 1}`} className="w-full h-full object-cover" />
                          <span className="absolute top-1 left-1 text-[9px] bg-black/50 text-white px-1 rounded">#{idx + 1}</span>
                        </div>
                        <div className="p-1.5 space-y-1">
                          <input value={m.caption || ''} onChange={e => setCaption(idx, e.target.value)} onBlur={() => saveMedia(media)} placeholder="Caption (optional)" className="w-full p-1 rounded border border-stone/40 text-[10px]" data-testid={`brand-media-caption-${idx}`} />
                          <div className="flex items-center justify-between">
                            <div className="flex gap-0.5">
                              <button onClick={() => moveMedia(idx, -1)} disabled={idx === 0} className="h-5 w-5 rounded flex items-center justify-center text-muted-foreground hover:bg-stone/20 disabled:opacity-30" title="Move up" data-testid={`brand-media-up-${idx}`}><ArrowUp className="h-3 w-3" /></button>
                              <button onClick={() => moveMedia(idx, 1)} disabled={idx === media.length - 1} className="h-5 w-5 rounded flex items-center justify-center text-muted-foreground hover:bg-stone/20 disabled:opacity-30" title="Move down" data-testid={`brand-media-down-${idx}`}><ArrowDown className="h-3 w-3" /></button>
                            </div>
                            <button onClick={() => removeMedia(idx)} className="h-5 w-5 rounded flex items-center justify-center text-red-500 hover:bg-red-50" title="Remove" data-testid={`brand-media-remove-${idx}`}><Trash2 className="h-3 w-3" /></button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </CardContent></Card>
          </div>
        ) : <Card className="bg-white border-stone/50"><CardContent className="p-8 text-center"><Globe className="h-8 w-8 mx-auto mb-2 text-muted-foreground/30" /><p className="text-sm text-muted-foreground">Select a brand page</p></CardContent></Card>}
      </div>
    </div>
  );
}
